import { useRef, useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { HOUR_HEIGHT, PX_PER_MIN, SNAP, minutesToLabel, formatDuration, eventsForDate, nowMinutes, todayStr, snapMinutes } from "@/lib/timeUtils";
import { Icon } from "@/lib/icons";
import { MapPin, Plus, CheckCircle2, Circle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function LiveIndicator({ dayStartMin, format }) {
  const [min, setMin] = useState(nowMinutes());
  useEffect(() => {
    const t = setInterval(() => setMin(nowMinutes()), 30000);
    return () => clearInterval(t);
  }, []);
  const top = (min - dayStartMin) * PX_PER_MIN;
  if (top < 0) return null;
  return (
    <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top }} data-testid="timeline-live-indicator">
      <div className="relative flex items-center" style={{ paddingLeft: 52 }}>
        <div className="absolute w-3 h-3 rounded-full pulse-ring" style={{ left: 46, background: "var(--timeline-indicator)" }} />
        <div className="w-3 h-3 rounded-full relative z-10" style={{ background: "var(--timeline-indicator)", boxShadow: "0 0 8px var(--timeline-indicator)" }} />
        <div className="flex-1 h-[1.5px]" style={{ background: "var(--timeline-indicator)" }} />
        <span className="absolute right-1 -top-5 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md tnum"
          style={{ background: "var(--timeline-indicator)", color: "#fff" }}>{minutesToLabel(min, format)}</span>
      </div>
    </div>
  );
}

export function DayTimeline({ dateStr, onEditEvent, onCreateAt }) {
  const { events, sharedEvents, settings, eventColor, eventsApi } = useApp();
  const format = settings?.time_format || "12";
  const dayStartHour = settings?.day_start_hour ?? 6;
  const dayEndHour = settings?.day_end_hour ?? 24;
  const dayStartMin = dayStartHour * 60;
  const totalHeight = (dayEndHour - dayStartHour) * HOUR_HEIGHT;
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null); // { id, start, end, mode }
  const isToday = dateStr === todayStr();

  const dayEvents = eventsForDate(events, dateStr);
  const sharedDay = eventsForDate(sharedEvents, dateStr);
  const hasShared = sharedDay.length > 0;

  const hours = [];
  for (let h = dayStartHour; h < dayEndHour; h++) hours.push(h);

  const yToMinutes = useCallback((clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const y = clientY - rect.top + containerRef.current.scrollTop;
    return dayStartMin + y / PX_PER_MIN;
  }, [dayStartMin]);

  const startDrag = (e, ev, mode) => {
    e.stopPropagation();
    e.preventDefault();
    const grabMin = yToMinutes(e.clientY);
    dragRef.current = { id: ev.id, mode, origStart: ev.start_minutes, origEnd: ev.end_minutes, grabMin };
    setDrag({ id: ev.id, start: ev.start_minutes, end: ev.end_minutes, mode });
  };

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const cur = yToMinutes(e.clientY);
      const delta = cur - d.grabMin;
      let start = d.origStart, end = d.origEnd;
      if (d.mode === "move") {
        const dur = d.origEnd - d.origStart;
        start = snapMinutes(d.origStart + delta);
        start = Math.max(0, Math.min(1440 - dur, start));
        end = start + dur;
      } else if (d.mode === "resize-end") {
        end = snapMinutes(d.origEnd + delta);
        end = Math.max(start + SNAP, Math.min(1440, end));
      } else if (d.mode === "resize-start") {
        start = snapMinutes(d.origStart + delta);
        start = Math.max(0, Math.min(end - SNAP, start));
      }
      setDrag({ id: d.id, start, end, mode: d.mode });
    };
    const up = async () => {
      const d = dragRef.current;
      if (d && drag) {
        dragRef.current = null;
        const final = drag;
        setDrag(null);
        if (final.start !== d.origStart || final.end !== d.origEnd) {
          await eventsApi.update(final.id, { start_minutes: final.start, end_minutes: final.end });
        }
      } else {
        dragRef.current = null;
        setDrag(null);
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [drag, eventsApi, yToMinutes]);

  const handleTrackClick = (e) => {
    if (dragRef.current) return;
    const min = snapMinutes(yToMinutes(e.clientY));
    onCreateAt?.(Math.max(0, Math.min(1440 - 60, min)));
  };

  return (
    <div ref={containerRef} className="relative overflow-y-auto overflow-x-hidden hide-scrollbar" style={{ maxHeight: "100%" }} data-testid="timeline-container">
      <div className="relative" style={{ height: totalHeight }}>
        {/* Hour grid */}
        {hours.map((h, i) => {
          const main = format === "24" ? String(h).padStart(2, "0") : String((h % 12) || 12);
          const sfx = format === "24" ? "" : (h < 12 ? "AM" : "PM");
          return (
            <div key={h} className="absolute left-0 right-0 flex items-center" style={{ top: i * HOUR_HEIGHT }}>
              <div className="w-[58px] pr-3 text-right shrink-0 -mt-2.5 leading-none">
                <span className="text-[13px] font-bold tnum font-display" style={{ color: "var(--text-secondary)" }}>{main}</span>
                {sfx && <span className="text-[9px] font-semibold ml-0.5" style={{ color: "var(--text-muted)" }}>{sfx}</span>}
              </div>
              <div className="flex-1 h-px" style={{ background: "var(--border-subtle)" }} />
            </div>
          );
        })}
        {/* 15-min sub lines */}
        {hours.map((h, i) => [15, 30, 45].map((m) => (
          <div key={`${h}-${m}`} className="absolute right-0 border-t" style={{ top: i * HOUR_HEIGHT + (m / 60) * HOUR_HEIGHT, left: 60, borderColor: "var(--border-subtle)", opacity: 0.35 }} />
        )))}

        {/* Timeline spine */}
        <div className="absolute top-0 bottom-0 w-[2px] rounded-full" style={{ left: 59, background: "var(--timeline-spine)" }} />

        {/* Click-to-create surface */}
        <div className="absolute inset-0 cursor-copy" style={{ left: 64 }} onClick={handleTrackClick} data-testid="timeline-create-surface" />

        {/* Events */}
        <AnimatePresence>
          {dayEvents.map((ev) => {
            const live = drag && drag.id === ev.id ? drag : null;
            const start = live ? live.start : ev.start_minutes;
            const end = live ? live.end : ev.end_minutes;
            const top = (start - dayStartMin) * PX_PER_MIN;
            const height = Math.max(22, (end - start) * PX_PER_MIN);
            const color = eventColor(ev);
            const small = height < 46;
            const completed = ev.completed;
            return (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2 }}
                className="absolute group no-select"
                style={{ top, height, left: 68, right: hasShared ? "50.5%" : 8, zIndex: live ? 40 : 10, cursor: "grab" }}
                onPointerDown={(e) => startDrag(e, ev, "move")}
                onClick={(e) => { e.stopPropagation(); if (!dragRef.current) onEditEvent(ev); }}
                data-testid="event-card-item"
              >
                <div className="relative h-full rounded-2xl overflow-hidden transition-all duration-200 group-hover:brightness-110"
                  style={{
                    background: completed
                      ? "var(--bg-card)"
                      : `linear-gradient(135deg, color-mix(in srgb, ${color} 28%, var(--bg-card)) 0%, color-mix(in srgb, ${color} 12%, var(--bg-card)) 100%)`,
                    border: `1px solid color-mix(in srgb, ${color} ${completed ? 14 : 36}%, transparent)`,
                    boxShadow: live ? `0 16px 40px ${color}66` : "0 4px 14px rgba(0,0,0,0.22)",
                    backdropFilter: "blur(8px)",
                    opacity: completed ? 0.62 : 1,
                  }}
                >
                  <div className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full" style={{ background: color }} />

                  {/* top resize handle */}
                  <div className="absolute top-0 left-3 right-3 h-2.5 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-start justify-center pt-0.5"
                    onPointerDown={(e) => startDrag(e, ev, "resize-start")} data-testid="event-resize-top">
                    <div className="w-8 h-1 rounded-full" style={{ background: color, opacity: 0.55 }} />
                  </div>

                  <div className={`h-full flex items-center gap-2.5 pl-3.5 pr-2 ${small ? "py-0" : "py-2"}`}>
                    {!small && ev.icon && (
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${color} 32%, transparent)` }}>
                        <Icon name={ev.icon} size={15} style={{ color }} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-[13px] truncate block" style={{ color: "var(--text-primary)", textDecoration: completed ? "line-through" : "none", opacity: completed ? 0.7 : 1 }}>{ev.title}</span>
                      {!small && (
                        <div className="flex items-center gap-2 mt-1 min-w-0">
                          <span className="text-[10px] font-mono tnum px-1.5 py-0.5 rounded-md" style={{ background: `color-mix(in srgb, ${color} 22%, transparent)`, color: "var(--text-primary)" }}>{minutesToLabel(start, format)}</span>
                          <span className="text-[10px] font-mono tnum" style={{ color: "var(--text-secondary)" }}>{formatDuration(end - start)}</span>
                          {ev.location && height > 66 && <span className="text-[10px] flex items-center gap-0.5 truncate" style={{ color: "var(--text-muted)" }}><MapPin size={9} />{ev.location}</span>}
                        </div>
                      )}
                    </div>
                    <button
                      className="shrink-0 rounded-full transition-transform hover:scale-110"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); eventsApi.update(ev.id, { completed: !ev.completed }); }}
                      data-testid="event-complete-toggle"
                      title={completed ? "Mark as not done" : "Mark as done"}
                    >
                      {completed
                        ? <CheckCircle2 size={20} className="check-pop" style={{ color }} />
                        : <Circle size={20} className="opacity-50 group-hover:opacity-90 transition-opacity" style={{ color: "var(--text-secondary)" }} />}
                    </button>
                  </div>

                  {/* bottom resize handle */}
                  <div className="absolute bottom-0 left-3 right-3 h-2.5 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-end justify-center pb-0.5"
                    onPointerDown={(e) => startDrag(e, ev, "resize-end")} data-testid="event-resize-bottom">
                    <div className="w-8 h-1 rounded-full" style={{ background: color, opacity: 0.55 }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Shared (friends') events — read-only overlay */}
        {hasShared && sharedDay.map((ev) => {
          const top = (ev.start_minutes - dayStartMin) * PX_PER_MIN;
          const height = Math.max(22, (ev.end_minutes - ev.start_minutes) * PX_PER_MIN);
          const color = ev._share_color || "var(--accent-primary)";
          const small = height < 46;
          return (
            <div key={`sh-${ev.id}`} className="absolute rounded-2xl overflow-hidden"
              style={{ top, height, left: "50.5%", right: 8, zIndex: 8, background: `color-mix(in srgb, ${color} 12%, var(--bg-card))`, border: `1.5px dashed ${color}`, backdropFilter: "blur(6px)" }}
              title={`${ev._owner_name}: ${ev.title}`} data-testid="shared-event-card">
              <div className={`h-full flex items-center gap-2 px-2.5 ${small ? "py-0" : "py-1.5"}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0" style={{ background: color }}>{ev._owner_name?.[0] || "?"}</div>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-[12px] truncate block" style={{ color: "var(--text-primary)", opacity: ev.completed ? 0.6 : 1, textDecoration: ev.completed ? "line-through" : "none" }}>{ev.title}</span>
                  {!small && <span className="text-[10px] font-mono tnum" style={{ color: "var(--text-secondary)" }}>{minutesToLabel(ev.start_minutes, format)}</span>}
                </div>
              </div>
            </div>
          );
        })}

        {isToday && <LiveIndicator dayStartMin={dayStartMin} format={format} />}

        {dayEvents.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ paddingLeft: 64 }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: "var(--bg-card)" }}>
              <Plus size={24} style={{ color: "var(--text-muted)" }} />
            </div>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Click anywhere to add an event</p>
          </div>
        )}
      </div>
    </div>
  );
}
